import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Loading from 'components/Loading';
import MessageBlock from 'components/MessageBlock';
import HeroLink from 'components/HeroLink';
import { getLocale } from 'selectors/user';
import { shouldShowGuestDirectory } from 'selectors/features';
import {
  getGuestDirectoryMenus,
  getGuestDirectoryIsLoading,
  getGuestDirectoryError,
} from 'selectors/guestDirectory';
import { trackEvent } from 'utils/tracking';

const MenusList = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  margin-top: 20px;
`;

const GuestDirectoryMenuSelection: React.FC = () => {
  const currentLocale = useSelector(getLocale);
  const featureEnabled = useSelector(shouldShowGuestDirectory);
  const isLoading = useSelector(getGuestDirectoryIsLoading);
  const error = useSelector(getGuestDirectoryError);
  const menus = useSelector(getGuestDirectoryMenus);

  if (!featureEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <Loading>
        <FormattedMessage defaultMessage="Loading Guest Directory..." />
      </Loading>
    );
  }

  if (error) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={<FormattedMessage defaultMessage="Unable to load guest directory. Please try again later." />}
      />
    );
  }

  if (menus.length === 0) {
    return (
      <MessageBlock
        header={<FormattedMessage defaultMessage="Sorry" />}
        body={
          <FormattedMessage defaultMessage="There are currently no guest directory menus available at this time. Please check back soon." />
        }
      />
    );
  }

  return (
    <MenusList className="container">
      {menus.map(menu => {
        const menuName = menu.translations?.[currentLocale]?.name || menu.name || '';
        const imagePath = menu.images?.[0]?.path || '';

        return (
          <HeroLink
            key={menu.id}
            url={`/guest-directory/menu/${menu.slug}`}
            image={imagePath}
            text={menuName}
            onClick={() => {
              trackEvent('select_menu', {
                category: 'guest_directory',
                menu_id: menu.id,
                menu_name: menuName,
              });
            }}
          />
        );
      })}
    </MenusList>
  );
};

export default GuestDirectoryMenuSelection;
